import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import { api, DOC_TYPES, STATUSES, computeTotals, fmtMoney, todayISO } from "@/lib/api";
import { useDocument, useSettings } from "@/hooks/useData";
import { DocumentPreview } from "@/components/DocumentPreview";
import { PreviewFrame } from "@/components/PreviewFrame";
import { LineItemsEditor } from "@/components/LineItemsEditor";
import { SignaturePad } from "@/components/SignaturePad";

const blank = (type, settings) => ({
  type,
  status: "draft",
  title: "",
  issue_date: todayISO(),
  due_date: "",
  party: { name: "", email: "", phone: "", address: "" },
  items: [{ description: "", quantity: 1, unit_price: 0 }],
  tax_rate: settings?.default_tax_rate ?? 0,
  discount: 0,
  payment_method: "",
  reference: "",
  notes: "",
  signature_file_id: "",
  signature_mode: "none",
  signer_name: "",
});

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="overline block mb-2">{label}</span>
    {children}
  </label>
);

export default function DocumentEditor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: settings } = useSettings();
  const { data: existing } = useDocument(id);
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id && existing) setDoc(existing);
    else if (!id && settings && !doc) setDoc(blank(params.get("type") || "invoice", settings));
  }, [id, existing, settings, params, doc]);

  if (!doc || !settings) return <div className="overline animate-pulse">Loading editor</div>;

  const set = (k, v) => setDoc((d) => ({ ...d, [k]: v }));
  const setParty = (k, v) => setDoc((d) => ({ ...d, party: { ...d.party, [k]: v } }));
  const meta = DOC_TYPES[doc.type];
  const isPayment = doc.type === "receipt" || doc.type === "voucher";
  const totals = computeTotals(doc);

  const save = async () => {
    if (!doc.party.name.trim()) return toast.error(`${meta.partyLabel} name is required`);
    setSaving(true);
    try {
      const payload = { ...doc, items: doc.items.filter((i) => i.description.trim() || Number(i.unit_price)).map((i) => ({ ...i, quantity: Number(i.quantity || 0), unit_price: Number(i.unit_price || 0) })), tax_rate: Number(doc.tax_rate || 0), discount: Number(doc.discount || 0) };
      const { data } = id ? await api.put(`/documents/${id}`, payload) : await api.post("/documents", payload);
      qc.invalidateQueries({ queryKey: ["documents"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.setQueryData(["document", data.id], data);
      toast.success(`${meta.label} ${id ? "updated" : "created"} — ${data.number}`);
      navigate(`/documents/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rise" data-testid="document-editor">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} data-testid="editor-back" className="h-10 w-10 grid place-items-center border border-border hover:border-foreground transition-colors"><ArrowLeft className="w-4 h-4" strokeWidth={1.5} /></button>
          <div>
            <p className="overline">{id ? "Editing" : "New"}</p>
            <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight">{meta.label} {doc.number && <span className="font-mono font-medium text-muted-foreground">{doc.number}</span>}</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:block text-right">
            <div className="overline">Total</div>
            <div className="font-mono text-xl font-semibold" data-testid="editor-total">{fmtMoney(totals.total, settings)}</div>
          </div>
          <button onClick={save} disabled={saving} data-testid="save-document-button" className="btn-press h-11 px-6 bg-primary text-primary-foreground font-semibold text-sm flex items-center gap-2 hover:bg-[#C04E2D] disabled:opacity-50">
            <Save className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Saving…" : id ? "Save changes" : `Create ${meta.label.toLowerCase()}`}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 space-y-8">
          {!id && (
            <div className="panel p-6">
              <span className="overline block mb-3">Document type</span>
              <div className="grid grid-cols-2 border border-border">
                {Object.entries(DOC_TYPES).map(([k, v]) => (
                  <button key={k} onClick={() => setDoc({ ...blank(k, settings), party: doc.party, items: doc.items })} data-testid={`type-${k}`} className={`h-11 text-sm font-medium border-border [&:nth-child(odd)]:border-r [&:nth-child(-n+2)]:border-b transition-colors ${doc.type === k ? "bg-foreground text-background" : "hover:bg-muted"}`}>{v.label}</button>
                ))}
              </div>
            </div>
          )}

          <div className="panel p-6 space-y-5">
            <h3 className="font-display text-lg font-bold">Details</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Title / subject" className="sm:col-span-2"><input className="field" data-testid="doc-title" value={doc.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Website redesign — phase 1" /></Field>
              <Field label={isPayment ? "Payment date" : "Issue date"}><input type="date" className="field font-mono" data-testid="doc-issue-date" value={doc.issue_date} onChange={(e) => set("issue_date", e.target.value)} /></Field>
              {!isPayment && <Field label={doc.type === "quotation" ? "Valid until" : "Due date"}><input type="date" className="field font-mono" data-testid="doc-due-date" value={doc.due_date} onChange={(e) => set("due_date", e.target.value)} /></Field>}
              <Field label="Status">
                <select className="field capitalize" data-testid="doc-status" value={doc.status} onChange={(e) => set("status", e.target.value)}>
                  {STATUSES[doc.type].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              {isPayment && <Field label="Payment method"><input className="field" data-testid="doc-payment-method" value={doc.payment_method} onChange={(e) => set("payment_method", e.target.value)} placeholder="Bank transfer, cash, card…" /></Field>}
              <Field label="Reference" className={isPayment ? "" : "sm:col-span-2"}><input className="field font-mono" data-testid="doc-reference" value={doc.reference} onChange={(e) => set("reference", e.target.value)} placeholder="PO number, cheque no., transaction id" /></Field>
            </div>
          </div>

          <div className="panel p-6 space-y-5">
            <h3 className="font-display text-lg font-bold">{meta.partyLabel}</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name" className="sm:col-span-2"><input className="field" data-testid="party-name" value={doc.party.name} onChange={(e) => setParty("name", e.target.value)} placeholder="Client or payee name" /></Field>
              <Field label="Email"><input className="field" data-testid="party-email" value={doc.party.email} onChange={(e) => setParty("email", e.target.value)} /></Field>
              <Field label="Phone"><input className="field" data-testid="party-phone" value={doc.party.phone} onChange={(e) => setParty("phone", e.target.value)} /></Field>
              <Field label="Address" className="sm:col-span-2"><textarea className="field-area" data-testid="party-address" value={doc.party.address} onChange={(e) => setParty("address", e.target.value)} /></Field>
            </div>
          </div>

          <div className="panel p-6 space-y-5">
            <h3 className="font-display text-lg font-bold">Line items</h3>
            <LineItemsEditor items={doc.items} onChange={(items) => set("items", items)} settings={settings} />
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
              <Field label={`${settings.tax_label} rate (%)`}><input type="number" min="0" step="0.01" className="field font-mono" data-testid="doc-tax-rate" value={doc.tax_rate} onChange={(e) => set("tax_rate", e.target.value === "" ? "" : Number(e.target.value))} /></Field>
              <Field label={`Discount (${settings.currency_code})`}><input type="number" min="0" step="0.01" className="field font-mono" data-testid="doc-discount" value={doc.discount} onChange={(e) => set("discount", e.target.value === "" ? "" : Number(e.target.value))} /></Field>
            </div>
            <div className="font-mono text-sm space-y-1 pt-2">
              <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span data-testid="editor-subtotal">{fmtMoney(totals.subtotal, settings)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>{settings.tax_label}</span><span data-testid="editor-tax">{fmtMoney(totals.tax_amount, settings)}</span></div>
              <div className="flex justify-between font-semibold text-base pt-2 border-t border-border"><span>Total</span><span>{fmtMoney(totals.total, settings)}</span></div>
            </div>
          </div>

          <div className="panel p-6 space-y-5">
            <h3 className="font-display text-lg font-bold">Notes</h3>
            <textarea className="field-area" data-testid="doc-notes" value={doc.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Terms, thanks, delivery details…" />
          </div>

          <div className="space-y-3">
            <h3 className="font-display text-lg font-bold">Signature</h3>
            <Field label="Signer name"><input className="field" data-testid="doc-signer-name" value={doc.signer_name} onChange={(e) => set("signer_name", e.target.value)} placeholder={settings.company_name || "Authorised signatory"} /></Field>
            <SignaturePad value={doc.signature_file_id} onChange={(v) => set("signature_file_id", v)} mode={doc.signature_mode} onModeChange={(m) => set("signature_mode", m)} hasManual={!!settings.manual_signature_file_id} />
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="lg:sticky lg:top-24">
            <p className="overline mb-3">Live preview</p>
            <div className="bg-muted p-4 sm:p-8 border border-border">
              <PreviewFrame>{(scale) => <DocumentPreview doc={doc} settings={settings} scale={scale} />}</PreviewFrame>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
